import type { ReactElement } from 'react';
import { AddIcon } from 'tdesign-icons-react';
import type { FormRule } from 'tdesign-react';
import { Form, Input, RadioGroup, Textarea } from 'tdesign-react';
import { AvatarUploadField } from './AvatarUploadField';
import {
  AVATAR_LABEL_MOBILE,
  AVATAR_UPLOAD_PLACEHOLDER,
  BIO_EN_PLACEHOLDER,
  BIO_ZH_PLACEHOLDER,
  CHINESE_NAME_LABEL,
  CONTACT_ALLOW_TEXT,
  CONTACT_DECLINE_TEXT,
  CONTACT_PERMISSION_LABEL,
  CONTACT_PERMISSION_REQUIRED_MESSAGE,
  EMAIL_LABEL,
  INTRODUCTION_EN_MAX_LENGTH,
  INTRODUCTION_MAX_LENGTH,
  MAX_TITLE_COUNT,
  MOBILE_FIELD_SECTION_CLASS,
  PERSONAL_PROFILE_LABEL,
  PROFILE_PICTURE_LABEL,
  PROFILE_PICTURE_PROMPT,
  PROFILE_PICTURE_REQUIRED_MESSAGE,
  TITLE_ADD_BUTTON_TEXT,
  TITLE_CN_PLACEHOLDER,
  TITLE_DELETE_ARIA_LABEL,
  TITLE_EN_PLACEHOLDER,
  TITLE_MAX_COUNT_MESSAGE,
  TITLE_ROW_COUNT,
} from './config';
import { createEmptyTitleRow } from './form-helpers';
import { DeleteActionIcon } from '../s-2026-signup/icons';
import { FieldLabel } from './shared';
import type { ConferenceTitleItem } from './types';

const { FormItem, FormList } = Form;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const createRequiredRule = (): FormRule => ({
  required: true,
  message: 'Required field',
  type: 'error',
});

const createLengthRule = (maxLength: number): FormRule => ({
  validator: (value) =>
    value ? Array.from(String(value)).length <= maxLength : true,
  message: `Please keep this within ${maxLength} characters.`,
  type: 'error',
});

const createTitleRequiredRule = ({
  form,
  index,
  isRequired,
  field,
}: {
  form: any;
  index: number;
  isRequired: boolean;
  field: 'titleCn' | 'titleEn';
}): FormRule => ({
  // 第一组头衔强制中英都填；后续组填写时中英任一项即可。
  validator: (value: unknown) => {
    if (isRequired) {
      return !!String(value ?? '').trim();
    }

    return true;
  },
  message: 'Required field',
  type: 'error',
});

export const createTextRules = ({
  maxLength,
  required = true,
}: {
  maxLength: number;
  required?: boolean;
}) => [
  ...(required ? [createRequiredRule()] : []),
  createLengthRule(maxLength),
];

export const emailRules = [
  {
    validator: (value: unknown) =>
      !value || EMAIL_PATTERN.test(String(value).trim()),
    message: 'Please enter a valid email address.',
    type: 'error',
  } as FormRule,
  createLengthRule(100),
];

type SignupTextareaProps = {
  autosize?: {
    minRows?: number;
    maxRows?: number;
  };
  className?: string;
  value?: string;
  maxlength?: number;
  placeholder: string;
  disabled?: boolean;
  onBlur?: (...args: any[]) => void;
  onChange?: (...args: any[]) => void;
  onFocus?: (...args: any[]) => void;
};

function SignupTextarea({
  autosize,
  className,
  value,
  maxlength = INTRODUCTION_MAX_LENGTH,
  placeholder,
  disabled = false,
  onBlur,
  onChange,
  onFocus,
}: SignupTextareaProps) {
  return (
    <Textarea
      autosize={autosize}
      className={className}
      value={value}
      maxlength={maxlength}
      allowInputOverMax
      placeholder={placeholder}
      disabled={disabled}
      onChange={(...args) => {
        onChange?.(...args);
      }}
      onFocus={(...args) => {
        onFocus?.(...args);
      }}
      onBlur={(...args) => {
        onBlur?.(...args);
      }}
    />
  );
}

export function DesktopAvatarField({
  onUploadStateChange,
  disabled = false,
}: {
  onUploadStateChange?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="pb-6">
      <AvatarUploadField
        label={<FieldLabel text={PROFILE_PICTURE_LABEL} required />}
        name="avatar"
        requiredRuleContent={PROFILE_PICTURE_REQUIRED_MESSAGE}
        uploadClassName="signup-2026-desktop-upload [&_.t-upload__placeholder]:text-sm"
        uploadWrapperClassName="w-[120px]"
        promptClassName="mt-2 text-xs leading-5 text-[rgba(0,0,0,0.4)]"
        prompt={PROFILE_PICTURE_PROMPT}
        onUploadStateChange={onUploadStateChange}
        disabled={disabled}
        placeholder={AVATAR_UPLOAD_PLACEHOLDER}
      />
    </div>
  );
}

export function MobileAvatarField({
  onUploadStateChange,
  disabled = false,
}: {
  onUploadStateChange?: () => void;
  disabled?: boolean;
}) {
  return (
    <AvatarUploadField
      label={<FieldLabel text={AVATAR_LABEL_MOBILE} required />}
      name="avatar"
      requiredRuleContent={PROFILE_PICTURE_REQUIRED_MESSAGE}
      promptClassName="mt-1 text-xs leading-5 text-[rgba(0,0,0,0.4)]"
      prompt={PROFILE_PICTURE_PROMPT}
      uploadClassName="signup-2026-mobile-upload"
      uploadWrapperClassName="w-[105px]"
      onUploadStateChange={onUploadStateChange}
      disabled={disabled}
      placeholder={AVATAR_UPLOAD_PLACEHOLDER}
      useCustomMobileUploadUi
    />
  );
}

export function DesktopInputField({
  label,
  name,
  placeholder,
  required = false,
  rules,
  maxlength,
  disabled = false,
}: {
  label: string;
  name: string;
  placeholder: string;
  required?: boolean;
  rules?: FormRule[];
  maxlength?: number;
  disabled?: boolean;
}) {
  return (
    <FormItem
      label={<FieldLabel text={label} required={required} />}
      name={name}
      rules={rules}
    >
      <Input
        size="medium"
        className="!h-[34px]"
        maxlength={maxlength}
        placeholder={placeholder}
        disabled={disabled}
      />
    </FormItem>
  );
}

export function DesktopBioFields({ disabled = false }: { disabled?: boolean }) {
  return (
    <div className="pb-6">
      <div className="flex flex-col gap-2">
        <FormItem
          label={<FieldLabel text="Personal Bio" required />}
          className="signup-2026-desktop-bio-en"
          name="bioEn"
          rules={[
            { required: true, message: 'Required field', type: 'error' },
            createLengthRule(INTRODUCTION_EN_MAX_LENGTH),
          ]}
        >
          <SignupTextarea
            className="!min-h-[74px]"
            placeholder={BIO_EN_PLACEHOLDER}
            disabled={disabled}
            maxlength={INTRODUCTION_EN_MAX_LENGTH}
          />
        </FormItem>
        <FormItem
          label={<FieldLabel text="Chinese Bio" required />}
          className="signup-2026-desktop-bio-zh"
          name="bioCn"
          rules={[
            { required: true, message: 'Required field', type: 'error' },
            createLengthRule(INTRODUCTION_MAX_LENGTH),
          ]}
        >
          <SignupTextarea
            className="!min-h-[54px]"
            placeholder={BIO_ZH_PLACEHOLDER}
            disabled={disabled}
            maxlength={600}
          />
        </FormItem>
      </div>
    </div>
  );
}

export function MobileInputField({
  label,
  name,
  placeholder,
  required = false,
  rules,
  maxlength,
  labelAction,
  suffixIcon,
  disabled = false,
}: {
  label: string;
  name: string | (string | number)[];
  placeholder: string;
  required?: boolean;
  rules?: FormRule[];
  maxlength?: number;
  labelAction?: ReactElement;
  suffixIcon?: ReactElement;
  disabled?: boolean;
}) {
  return (
    <div className={MOBILE_FIELD_SECTION_CLASS}>
      <div className="signup-2026-mobile-field-label-wrapper flex items-center justify-between gap-3">
        <FieldLabel text={label} required={required} />
        {labelAction ?? null}
      </div>
      <FormItem name={name} rules={rules}>
        <Input
          size="large"
          maxlength={maxlength}
          allowInputOverMax
          placeholder={placeholder}
          suffixIcon={suffixIcon}
          disabled={disabled}
        />
      </FormItem>
    </div>
  );
}

export function MobileTextareaField({
  label,
  name,
  placeholder,
  required = false,
  rules,
  disabled = false,
  maxlength,
}: {
  label: string;
  name: string;
  placeholder: string;
  required?: boolean;
  rules?: FormRule[];
  disabled?: boolean;
  maxlength?: number;
}) {
  return (
    <>
      <div className="signup-2026-mobile-field-label-wrapper signup-2026-mobile-textarea-label">
        <FieldLabel text={label} required={required} />
      </div>
      <FormItem name={name} rules={rules}>
        <SignupTextarea
          className="signup-2026-mobile-textarea-field"
          placeholder={placeholder}
          disabled={disabled}
          maxlength={maxlength}
        />
      </FormItem>
    </>
  );
}

export function TitleListFields({
  form,
  mobile = false,
  disabled = false,
}: {
  form: any;
  mobile?: boolean;
  disabled?: boolean;
}) {
  return (
    <FormList name="titles">
      {(fields) => (
        <>
          {fields.map(({ key, name }) => {
            const index = Number(name);
            const isRequired = index === 0;
            const label =
              index === 0 ? 'Primary Title' : 'Additional Title (optional)';

            if (mobile) {
              return (
                <div key={key}>
                  <MobileInputField
                    label={label}
                    name={[name, 'titleEn']}
                    placeholder={
                      isRequired
                        ? 'Primary Title in English'
                        : TITLE_EN_PLACEHOLDER
                    }
                    required={isRequired}
                    rules={[
                      createTitleRequiredRule({
                        form,
                        index,
                        isRequired,
                        field: 'titleEn',
                      }),
                      createLengthRule(100),
                    ]}
                    maxlength={100}
                    labelAction={
                      !disabled && index >= TITLE_ROW_COUNT ? (
                        <button
                          type="button"
                          aria-label={TITLE_DELETE_ARIA_LABEL}
                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[rgba(0,0,0,0.4)]"
                          onClick={() => {
                            const currentTitles =
                              form.getFieldValue('titles') || [];
                            form.setFieldsValue({
                              titles: currentTitles.filter(
                                (_: any, i: number) => i !== index
                              ),
                            });
                          }}
                        >
                          <span className="scale-[0.8]">
                            <DeleteActionIcon />
                          </span>
                        </button>
                      ) : undefined
                    }
                    disabled={disabled}
                  />
                  <MobileInputField
                    label={`${label} (Chinese)`}
                    name={[name, 'titleCn']}
                    placeholder={
                      isRequired
                        ? 'Primary Title in Chinese'
                        : TITLE_CN_PLACEHOLDER
                    }
                    required={isRequired}
                    rules={[
                      createTitleRequiredRule({
                        form,
                        index,
                        isRequired,
                        field: 'titleCn',
                      }),
                      createLengthRule(50),
                    ]}
                    maxlength={50}
                    disabled={disabled}
                  />
                </div>
              );
            }

            return (
              <div key={key} className="pb-6">
                <div className="mb-2 flex items-center justify-between">
                  <FieldLabel text={label} required={isRequired} />
                  {!disabled && index >= TITLE_ROW_COUNT ? (
                    <button
                      type="button"
                      aria-label={TITLE_DELETE_ARIA_LABEL}
                      className="inline-flex h-5 w-5 items-center justify-center"
                      onClick={() => {
                        const currentTitles =
                          form.getFieldValue('titles') || [];
                        form.setFieldsValue({
                          titles: currentTitles.filter(
                            (_: any, i: number) => i !== index
                          ),
                        });
                      }}
                    >
                      <DeleteActionIcon />
                    </button>
                  ) : null}
                </div>
                <div className="flex gap-8">
                  <div className="w-[400px]">
                    <FormItem
                      name={[name, 'titleEn']}
                      rules={[
                        createTitleRequiredRule({
                          form,
                          index,
                          isRequired,
                          field: 'titleEn',
                        }),
                        createLengthRule(100),
                      ]}
                    >
                      <Input
                        size="medium"
                        className="!h-[34px]"
                        maxlength={100}
                        disabled={disabled}
                        placeholder={
                          isRequired
                            ? 'Primary Title in English'
                            : TITLE_EN_PLACEHOLDER
                        }
                      />
                    </FormItem>
                  </div>
                  <div className="w-[400px]">
                    <FormItem
                      name={[name, 'titleCn']}
                      rules={[
                        createTitleRequiredRule({
                          form,
                          index,
                          isRequired,
                          field: 'titleCn',
                        }),
                        createLengthRule(50),
                      ]}
                    >
                      <Input
                        size="medium"
                        className="!h-[34px]"
                        maxlength={50}
                        disabled={disabled}
                        placeholder={
                          isRequired
                            ? 'Primary Title in Chinese'
                            : TITLE_CN_PLACEHOLDER
                        }
                      />
                    </FormItem>
                  </div>
                </div>
              </div>
            );
          })}

          {!disabled &&
            (mobile ? (
              <div className={MOBILE_FIELD_SECTION_CLASS}>
                <button
                  type="button"
                  disabled={fields.length >= MAX_TITLE_COUNT}
                  className={`inline-flex h-6 items-center gap-1 rounded-full border border-[#DCDCDC] bg-white px-[10px] text-xs leading-4 ${
                    fields.length >= MAX_TITLE_COUNT
                      ? 'cursor-not-allowed text-[rgba(0,0,0,0.4)]'
                      : 'text-[rgba(0,0,0,0.9)]'
                  }`}
                  onClick={() => {
                    const currentTitles = form.getFieldValue('titles') || [];
                    form.setFieldsValue({
                      titles: [...currentTitles, createEmptyTitleRow()],
                    });
                  }}
                  title={
                    fields.length >= MAX_TITLE_COUNT
                      ? TITLE_MAX_COUNT_MESSAGE
                      : undefined
                  }
                >
                  <AddIcon size={12} color="currentColor" />
                  {TITLE_ADD_BUTTON_TEXT}
                </button>
                <span className="ml-2 text-xs text-[rgba(0,0,0,0.4)]">
                  {TITLE_MAX_COUNT_MESSAGE}
                </span>
              </div>
            ) : (
              <div className="pb-6">
                <button
                  type="button"
                  disabled={fields.length >= MAX_TITLE_COUNT}
                  className={`inline-flex items-center justify-center gap-2 rounded-[3px] border border-[#DCDCDC] bg-white px-4 py-[5px] text-sm leading-[22px] ${
                    fields.length >= MAX_TITLE_COUNT
                      ? 'cursor-not-allowed text-[rgba(0,0,0,0.4)]'
                      : 'text-[rgba(0,0,0,0.9)]'
                  }`}
                  onClick={() => {
                    const currentTitles = form.getFieldValue('titles') || [];
                    form.setFieldsValue({
                      titles: [...currentTitles, createEmptyTitleRow()],
                    });
                  }}
                  title={
                    fields.length >= MAX_TITLE_COUNT
                      ? TITLE_MAX_COUNT_MESSAGE
                      : undefined
                  }
                >
                  <AddIcon size={16} color="currentColor" />
                  {TITLE_ADD_BUTTON_TEXT}
                </button>
                <span className="ml-3 text-sm text-[rgba(0,0,0,0.4)]">
                  {TITLE_MAX_COUNT_MESSAGE}
                </span>
              </div>
            ))}
        </>
      )}
    </FormList>
  );
}

export function ContactPermissionField({
  desktop = false,
  disabled = false,
}: {
  desktop?: boolean;
  disabled?: boolean;
}) {
  const options = [
    {
      label: CONTACT_ALLOW_TEXT,
      value: 1,
    },
    {
      label: CONTACT_DECLINE_TEXT,
      value: 0,
    },
  ];
  const rules = [
    {
      required: true,
      message: CONTACT_PERMISSION_REQUIRED_MESSAGE,
      type: 'error',
    } as FormRule,
  ];

  if (!desktop) {
    return (
      <div className={MOBILE_FIELD_SECTION_CLASS}>
        <div className="signup-2026-mobile-field-label-wrapper">
          <FieldLabel text={CONTACT_PERMISSION_LABEL} required />
        </div>
        <FormItem name="allowContact" rules={rules}>
          <RadioGroup
            options={options}
            className="flex items-center"
            disabled={disabled}
          />
        </FormItem>
      </div>
    );
  }

  return (
    <FormItem
      label={<FieldLabel text={CONTACT_PERMISSION_LABEL} required />}
      name="allowContact"
      rules={rules}
    >
      <RadioGroup options={options} disabled={disabled} />
    </FormItem>
  );
}
